/**
 * Double Ratchet.
 *
 * Reference: https://signal.org/docs/specifications/doubleratchet/
 *
 * Two ratchets turn together. The symmetric ratchet advances a chain key per
 * message so a stolen message key opens exactly one message. The DH ratchet
 * turns whenever the peer sends a new public key, which re-seeds the root key
 * and gives the session self-healing: an attacker who steals all state still
 * loses the conversation as soon as one uncompromised round trip happens.
 */
import {
  type KeyPair,
  aeadDecrypt,
  aeadEncrypt,
  concatBytes,
  dh,
  fromB64,
  generateDHKeyPair,
  hmac,
  kdf,
  KEY_LEN,
  NONCE_LEN,
  sha256,
  toB64,
  utf8,
} from './primitives.ts';
import type { RatchetHeader, RatchetMessage } from '../types.ts';

/** How far ahead of the expected counter we will jump within one chain. */
export const MAX_SKIP = 1000;
/** Hard cap on retained out-of-order keys, so a peer cannot exhaust memory. */
export const MAX_SKIPPED_STORED = 2000;

export interface RatchetState {
  /** Our current ratchet keypair. */
  DHs: KeyPair;
  /** Their current ratchet public key; absent until we hear from them. */
  DHr?: Uint8Array;
  RK: Uint8Array;
  CKs?: Uint8Array;
  CKr?: Uint8Array;
  Ns: number;
  Nr: number;
  PN: number;
  /** Out-of-order message keys, keyed by `${theirRatchetPub}:${n}`. */
  MKSKIPPED: Map<string, Uint8Array>;
  /** Insertion order for MKSKIPPED, used to evict oldest past the cap. */
  skippedOrder: string[];
  AD: Uint8Array;
}

// ---------------------------------------------------------------------------
// KDFs
// ---------------------------------------------------------------------------

/** Root KDF: consumes a DH output, emits the next root key and a chain key. */
function kdfRK(rk: Uint8Array, dhOut: Uint8Array): { rk: Uint8Array; ck: Uint8Array } {
  const out = kdf(dhOut, rk, 'VAV-RootChain-v1', 64);
  return { rk: out.slice(0, 32), ck: out.slice(32, 64) };
}

/**
 * Chain KDF. Distinct constants keep the message key and the next chain key
 * independent, so revealing one message key says nothing about the next.
 */
function kdfCK(ck: Uint8Array): { ck: Uint8Array; mk: Uint8Array } {
  return {
    mk: hmac(sha256, ck, new Uint8Array([0x01])),
    ck: hmac(sha256, ck, new Uint8Array([0x02])),
  };
}

/** Expand a message key into an AEAD key + nonce. Each is used exactly once. */
function messageKeyMaterial(mk: Uint8Array): { key: Uint8Array; nonce: Uint8Array } {
  const out = kdf(mk, new Uint8Array(32), 'VAV-MessageKey-v1', KEY_LEN + NONCE_LEN);
  return { key: out.slice(0, KEY_LEN), nonce: out.slice(KEY_LEN) };
}

/** Deterministic header encoding, authenticated as AEAD associated data. */
function encodeHeader(hdr: RatchetHeader): Uint8Array {
  const counters = new Uint8Array(8);
  const view = new DataView(counters.buffer);
  view.setUint32(0, hdr.pn, false);
  view.setUint32(4, hdr.n, false);
  return concatBytes(fromB64(hdr.dh), counters);
}

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------

/**
 * Initiator. Their signed prekey doubles as their first ratchet public key,
 * which is what lets the very first message already be sent under a ratcheted
 * chain rather than raw X3DH output.
 */
export function initRatchetInitiator(
  sharedSecret: Uint8Array,
  theirSignedPreKeyPub: Uint8Array,
  associatedData: Uint8Array,
): RatchetState {
  const DHs = generateDHKeyPair();
  const { rk, ck } = kdfRK(sharedSecret, dh(DHs.priv, theirSignedPreKeyPub));
  return {
    DHs,
    DHr: theirSignedPreKeyPub,
    RK: rk,
    CKs: ck,
    Ns: 0,
    Nr: 0,
    PN: 0,
    MKSKIPPED: new Map(),
    skippedOrder: [],
    AD: associatedData,
  };
}

/**
 * Responder. Starts with no sending chain: the first thing this side does must
 * be a decrypt, which performs the DH ratchet step that creates one.
 */
export function initRatchetResponder(
  sharedSecret: Uint8Array,
  ourSignedPreKey: KeyPair,
  associatedData: Uint8Array,
): RatchetState {
  return {
    DHs: ourSignedPreKey,
    RK: sharedSecret,
    Ns: 0,
    Nr: 0,
    PN: 0,
    MKSKIPPED: new Map(),
    skippedOrder: [],
    AD: associatedData,
  };
}

// ---------------------------------------------------------------------------
// Encrypt / decrypt
// ---------------------------------------------------------------------------

export function ratchetEncrypt(state: RatchetState, plaintext: Uint8Array): RatchetMessage {
  if (!state.CKs) {
    throw new Error('ratchet: cannot send before receiving the peer\'s first message');
  }
  const { ck, mk } = kdfCK(state.CKs);
  const hdr: RatchetHeader = { dh: toB64(state.DHs.pub), pn: state.PN, n: state.Ns };
  const { key, nonce } = messageKeyMaterial(mk);
  const ct = aeadEncrypt(key, nonce, plaintext, concatBytes(state.AD, encodeHeader(hdr)));

  state.CKs = ck;
  state.Ns += 1;
  return { hdr, ct: toB64(ct) };
}

/**
 * Decrypt.
 *
 * Mutations are staged on a shallow clone and only committed once the AEAD tag
 * verifies. A forged or corrupted message therefore cannot advance — or
 * destroy — the real session state, which is the difference between dropping
 * one bad packet and permanently breaking a conversation.
 */
export function ratchetDecrypt(state: RatchetState, message: RatchetMessage): Uint8Array {
  const draft = cloneState(state);
  const plaintext = decryptInto(draft, message);
  commitState(state, draft);
  return plaintext;
}

function decryptInto(state: RatchetState, message: RatchetMessage): Uint8Array {
  const { hdr } = message;
  const ct = fromB64(message.ct);
  const aad = concatBytes(state.AD, encodeHeader(hdr));

  const skippedKey = `${hdr.dh}:${hdr.n}`;
  const skipped = state.MKSKIPPED.get(skippedKey);
  if (skipped) {
    const { key, nonce } = messageKeyMaterial(skipped);
    const plaintext = aeadDecrypt(key, nonce, ct, aad);
    // Consume it: replaying the same ciphertext must not decrypt twice.
    state.MKSKIPPED.delete(skippedKey);
    state.skippedOrder = state.skippedOrder.filter((k) => k !== skippedKey);
    return plaintext;
  }

  const theirDH = fromB64(hdr.dh);
  if (!state.DHr || !bytesEqual(theirDH, state.DHr)) {
    skipMessageKeys(state, hdr.pn);
    dhRatchet(state, theirDH);
  }
  skipMessageKeys(state, hdr.n);

  if (!state.CKr) throw new Error('ratchet: no receiving chain');
  const { ck, mk } = kdfCK(state.CKr);
  const { key, nonce } = messageKeyMaterial(mk);
  const plaintext = aeadDecrypt(key, nonce, ct, aad);

  state.CKr = ck;
  state.Nr += 1;
  return plaintext;
}

/**
 * Advance the receiving chain to `until`, banking the keys we stepped over so
 * that messages delayed by the network still decrypt when they show up.
 */
function skipMessageKeys(state: RatchetState, until: number): void {
  if (state.Nr + MAX_SKIP < until) {
    throw new Error(`ratchet: refusing to skip ${until - state.Nr} messages`);
  }
  if (!state.CKr) return;
  while (state.Nr < until) {
    const { ck, mk } = kdfCK(state.CKr);
    const key = `${toB64(state.DHr!)}:${state.Nr}`;
    state.MKSKIPPED.set(key, mk);
    state.skippedOrder.push(key);
    state.CKr = ck;
    state.Nr += 1;
  }
  while (state.skippedOrder.length > MAX_SKIPPED_STORED) {
    const oldest = state.skippedOrder.shift()!;
    state.MKSKIPPED.delete(oldest);
  }
}

/** A DH ratchet step: new receiving chain, new keypair, new sending chain. */
function dhRatchet(state: RatchetState, theirDH: Uint8Array): void {
  state.PN = state.Ns;
  state.Ns = 0;
  state.Nr = 0;
  state.DHr = theirDH;

  const recv = kdfRK(state.RK, dh(state.DHs.priv, state.DHr));
  state.RK = recv.rk;
  state.CKr = recv.ck;

  state.DHs = generateDHKeyPair();
  const send = kdfRK(state.RK, dh(state.DHs.priv, state.DHr));
  state.RK = send.rk;
  state.CKs = send.ck;
}

// ---------------------------------------------------------------------------
// State plumbing
// ---------------------------------------------------------------------------

function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function cloneState(state: RatchetState): RatchetState {
  return {
    DHs: { priv: state.DHs.priv, pub: state.DHs.pub },
    DHr: state.DHr,
    RK: state.RK,
    CKs: state.CKs,
    CKr: state.CKr,
    Ns: state.Ns,
    Nr: state.Nr,
    PN: state.PN,
    MKSKIPPED: new Map(state.MKSKIPPED),
    skippedOrder: [...state.skippedOrder],
    AD: state.AD,
  };
}

function commitState(target: RatchetState, source: RatchetState): void {
  Object.assign(target, source);
}

// ---------------------------------------------------------------------------
// Serialization (for persisting a session between app launches)
// ---------------------------------------------------------------------------

export interface SerializedRatchet {
  DHs: { priv: string; pub: string };
  DHr?: string;
  RK: string;
  CKs?: string;
  CKr?: string;
  Ns: number;
  Nr: number;
  PN: number;
  skipped: [string, string][];
  AD: string;
}

export function serializeRatchet(state: RatchetState): SerializedRatchet {
  return {
    DHs: { priv: toB64(state.DHs.priv), pub: toB64(state.DHs.pub) },
    DHr: state.DHr ? toB64(state.DHr) : undefined,
    RK: toB64(state.RK),
    CKs: state.CKs ? toB64(state.CKs) : undefined,
    CKr: state.CKr ? toB64(state.CKr) : undefined,
    Ns: state.Ns,
    Nr: state.Nr,
    PN: state.PN,
    // Ordered, so the eviction queue survives a reload.
    skipped: state.skippedOrder.map((k) => [k, toB64(state.MKSKIPPED.get(k)!)]),
    AD: toB64(state.AD),
  };
}

export function deserializeRatchet(s: SerializedRatchet): RatchetState {
  const MKSKIPPED = new Map<string, Uint8Array>();
  for (const [k, v] of s.skipped) MKSKIPPED.set(k, fromB64(v));
  return {
    DHs: { priv: fromB64(s.DHs.priv), pub: fromB64(s.DHs.pub) },
    DHr: s.DHr ? fromB64(s.DHr) : undefined,
    RK: fromB64(s.RK),
    CKs: s.CKs ? fromB64(s.CKs) : undefined,
    CKr: s.CKr ? fromB64(s.CKr) : undefined,
    Ns: s.Ns,
    Nr: s.Nr,
    PN: s.PN,
    MKSKIPPED,
    skippedOrder: s.skipped.map(([k]) => k),
    AD: fromB64(s.AD),
  };
}

/** Exposed for tests; not part of the public messaging API. */
export const __internal = { kdfRK, kdfCK, encodeHeader, messageKeyMaterial, utf8 };
